import { supabase } from './supabase.js';

export const collegeAccessControlService = {
  // Get college access settings for a child
  async getSettings(childId) {
    const { data, error } = await supabase
      .from('college_access_settings')
      .select('*')
      .eq('child_id', childId)
      .single();
    
    if (error && error.code !== 'PGRST116') { // Not found error
      throw error;
    }
    
    // Return default settings if none exist
    if (!data) {
      return {
        child_id: childId,
        has_college_hub_access: true,
        can_access_assessments: true,
        can_view_progress: true,
        can_modify_preferences: true,
        additional_settings: {}
      };
    }
    
    return data;
  },

  // Update college access settings
  async updateSettings(childId, updates) {
    const { data: existing } = await supabase
      .from('college_access_settings')
      .select('*')
      .eq('child_id', childId)
      .single();
    
    if (existing) {
      // Update existing settings
      const { data, error } = await supabase
        .from('college_access_settings')
        .update(updates)
        .eq('child_id', childId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } else {
      // Create new settings
      const { data, error } = await supabase
        .from('college_access_settings')
        .insert([{ 
          child_id: childId, 
          ...updates 
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    }
  },

  // Check if a child has access to the college hub
  async hasCollegeHubAccess(childId) {
    const settings = await this.getSettings(childId);
    return settings.has_college_hub_access;
  },

  // Check specific permission for a child
  async hasPermission(childId, permission) {
    const settings = await this.getSettings(childId);
    return settings[permission] || false;
  },

  // Get all college access settings
  async getAllSettings() {
    const { data, error } = await supabase
      .from('college_access_settings')
      .select('*');
    
    if (error) throw error;
    return data || [];
  },

  // Get family settings (single-family architecture - same as getAllSettings)
  async getFamilySettings(familyId = null) {
    // In single-family architecture, family ID is ignored
    const { data, error } = await supabase
      .from('college_access_settings')
      .select('*');
    
    if (error) throw error;
    return data || [];
  },

  // Get all children with college hub access
  async getChildrenWithAccess() {
    const { data, error } = await supabase
      .from('college_access_settings')
      .select('*')
      .eq('has_college_hub_access', true);
    
    if (error) throw error;
    return data || [];
  },

  // Update access for a specific child (convenience method)
  async setCollegeHubAccess(childId, hasAccess) {
    return this.updateSettings(childId, {
      has_college_hub_access: hasAccess
    });
  },

  // Bulk update access for multiple children
  async bulkUpdateAccess(childrenAccess) {
    const updates = Object.entries(childrenAccess).map(([childId, hasAccess]) => ({
      child_id: childId,
      has_college_hub_access: hasAccess
    }));

    const { data, error } = await supabase
      .from('college_access_settings')
      .upsert(updates, {
        onConflict: 'child_id'
      })
      .select();

    if (error) throw error;
    return data;
  },

  // Get simplified access map (childId -> boolean) for UI compatibility
  async getAccessMap() {
    const settings = await this.getAllSettings();
    const accessMap = {};
    
    settings.forEach(setting => {
      accessMap[setting.child_id] = setting.has_college_hub_access;
    });
    
    return accessMap;
  },

  // Set access map from UI (for localStorage compatibility)
  async setAccessMap(accessMap) {
    return this.bulkUpdateAccess(Object.fromEntries(
      Object.entries(accessMap)
    ));
  },

  // Get module access for UI compatibility (matches CollegeAccessControl expectations)
  async getModuleAccess(moduleName) {
    const settings = await this.getAllSettings();
    return settings.map(setting => ({
      user_id: setting.child_id,
      has_access: setting.has_college_hub_access
    }));
  },

  // Update module access for UI compatibility (matches CollegeAccessControl expectations)
  async updateModuleAccess(childId, moduleName, hasAccess) {
    return this.setCollegeHubAccess(childId, hasAccess);
  }
};